import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import * as xlsx from 'xlsx';
import { gql, useMutation } from '@apollo/client';
import { Box, Button, TextField } from '@mui/material';
import Dropdown from '../../common/formControls/Dropdown';
import { useQuery } from '@apollo/client';
import { PriceGridContext } from '../../../context/PriceGridContext';
import { selectProject } from '../../../features/project/projectSlice';
import { IUnit, IPriceList } from '../../../types/unit';
import { FlexEnd } from '../../../commonStyles';
import { useAppDispatch } from '../../../app/hooks';
import { handleModal } from '../../../features/modal/modalSlice';
import { showSuccessSnackbar, showErrorSnackbar } from '../../../features/snackbar/snackbarSlice';
import { keyToCamel } from '../../../utils/Functions';

const Draft = () => {
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const {
    priceLists,
    setPriceLists,
    selectedPriceList,
    setSelectedPriceList,
    units,
    setUnits,
    setDraft,
    setModalType,
  } = useContext(PriceGridContext);
  // Local rather than context state — this only needs to live for as long as the modal is open,
  // and keeping it in the shared PriceGridContext meant every keystroke re-rendered the whole
  // price grid tree (every context consumer re-renders on any context value change), which is
  // what made typing in this field laggy.
  const [priceListName, setPriceListName] = useState<string>('');

  useQuery(GETPRICELISTS, {
    fetchPolicy: 'cache-and-network',
    variables: { filter: { project: project._id } },
    onCompleted: (data) => {
      setPriceLists(data.priceListMany);
    },
  });

  const [updatePriceList] = useMutation(UPDATEPRICELIST, {
    onCompleted: (data) => {
      setSelectedPriceList({
        ...selectedPriceList!,
        name: data.priceListUpdateById.record.name,
      });
    },
  });

  const [createPriceList] = useMutation(CREATEPRICELIST, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Price List Created!'));
      setUnits(data.priceListCreateOne.record.draft);
      setDraft(true);
      setSelectedPriceList(data.priceListCreateOne.record);
      setPriceLists([...priceLists, data.priceListCreateOne.record]);
      setPriceListName(data.priceListCreateOne.record.name);
      storeDispatch(handleModal(false));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [removePriceList] = useMutation(DELETEPRICELIST, {
    onCompleted: (data) => {
      setPriceLists(priceLists.filter((priceList: IPriceList) => priceList._id !== data.priceListRemoveById.record._id));
      setSelectedPriceList(null);
      setPriceListName('');
      storeDispatch(showSuccessSnackbar(`${data.priceListRemoveById.record.name} has been deleted`));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const handlePriceListDropdown = (e: any) => {
    let selectedPriceList = priceLists.find((priceList: IPriceList) => priceList.name === e.target.value);
    if (selectedPriceList) {
      setSelectedPriceList(selectedPriceList);
      setPriceListName(selectedPriceList.name);
    }
  };

  const viewSelected = async () => {
    storeDispatch(handleModal(false));
    if (priceListName !== selectedPriceList?.name) {
      await updatePriceList({ variables: { _id: selectedPriceList?._id, record: { name: priceListName } } });
    }
    setDraft(true);
    setUnits(selectedPriceList?.draft);
  };

  const readUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (event.target.files) {
      let name = priceListName ? priceListName : `Upload Draft ${priceLists.length}`;
      const reader = new FileReader();
      reader.onload = async (event: any) => {
        const data = event.target.result;
        const workbook = xlsx.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet);
        let customData = keyToCamel([...json]).map((data: any) => {
          let selectedUnit = units.find((unit: IUnit) => unit.suite === data.suite.toString());
          return {
            status: data.status,
            unitId: selectedUnit ? selectedUnit._id : '',
            suite: data.suite.toString(),
            unit: data.unit.toString(),
            level: data.level.toString(),
            modelType: data.modelType ? data.modelType : '',
            unitType: data.unitType ? data.unitType : '',
            basePrice: data.basePrice ? data.basePrice : '',
            size: data.size ? data.size : '',
            outdoor: data.outdoorType || data.outdoorSize ? [{ name: data.outdoorType || '', size: data.outdoorSize || 0 }] : [],
            bathroom: data.bathroom,
            exposure: data.exposure,
            ceilingHeight: data.ceilingHeight ? data.ceilingHeight.toString() : selectedUnit ? selectedUnit.ceilingHeight : '',
            col: selectedUnit.col,
            row: selectedUnit.row,
            stackCol: selectedUnit.stackCol,
            stackRow: selectedUnit.stackRow,
            tier: data.tier ? data.tier : selectedUnit.tier ? selectedUnit.tier : '',
          };
        });

        await createPriceList({
          variables: {
            record: {
              name: `${name ? name : `Price List Draft ${priceLists.length + 1}`}`,
              project: project._id,
              draft: customData,
            },
          },
        });
      };
      reader.readAsArrayBuffer(event.target.files[0]);
    }
  };

  const createList = async () => {
    if (selectedPriceList) {
      let name = selectedPriceList.name === priceListName ? `${priceListName} Copy` : priceListName;
      await createPriceList({
        variables: {
          record: {
            name: `${name ? name : `Price List Draft ${priceLists.length + 1}`}`,
            project: project._id,
            draft: units,
          },
        },
      });
    } else {
      let priceListUnits = await units.map((unit: IUnit) => {
        return {
          status: unit.status,
          suite: unit.suite,
          unitId: unit._id,
          unit: unit.unit,
          level: unit.level,
          modelType: unit.modelType,
          unitType: unit.unitType,
          basePrice: unit.basePrice,
          size: unit.size,
          outdoor: unit.outdoor,
          bathroom: unit.bathroom,
          exposure: unit.exposure,
          ceilingHeight: unit.ceilingHeight,
          custom: unit.custom,
          col: unit.col,
          row: unit.row,
          stackCol: unit.stackCol,
          stackRow: unit.stackRow,
          tier: unit.tier,
        };
      });
      await createPriceList({
        variables: {
          record: {
            name: `${priceListName ? priceListName : `Price List Draft ${priceLists.length + 1}`}`,
            project: project._id,
            draft: priceListUnits,
          },
        },
      });
    }
  };

  return (
    <Box sx={{ my: 2 }}>
      <Box sx={{ mb: 2 }}>
        <strong>
          Select "Create New Price List" to create an editable draft of the default price list, or select from an existing draft from the
          dropdown to create an editable copy.
        </strong>
      </Box>
      <Box>
        <strong>If you are uploading an Excel, the column headers (case-sensitive as below) are:</strong>
        <ul>
          <li>level</li>
          <li>unit</li>
          <li>suite</li>
          <li>unitType</li>
          <li>modelType</li>
          <li>bathroom</li>
          <li>size</li>
          <li>exposure</li>
          <li>basePrice</li>
          <li>outdoorType (Optional)</li>
          <li>outdoorSize (Optional)</li>
          <li>ceilingHeight (Optional)</li>
          <li>tier (Optional)</li>
        </ul>
      </Box>
      <Dropdown
        title={'Price Lists'}
        menuList={priceLists.map((priceList: IPriceList) => priceList.name)}
        name={'priceLists'}
        handleSelect={(e: any) => handlePriceListDropdown(e)}
        value={selectedPriceList ? selectedPriceList.name : ''}
      />
      <Box sx={{ mt: 2 }}>
        <TextField
          title={'Name'}
          name={'priceListName'}
          fullWidth
          value={priceListName}
          label={'Name'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriceListName(e.target.value)}
        />
      </Box>
      <FlexEnd sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="info"
          sx={{ mr: 1 }}
          onClick={() => {
            if (selectedPriceList) {
              setSelectedPriceList(null);
            }
            setModalType('');
            storeDispatch(handleModal(false));
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          sx={{ mr: 1 }}
          onClick={() => {
            if (!selectedPriceList) return storeDispatch(showErrorSnackbar('No Price List Selected'));
            removePriceList({ variables: { _id: selectedPriceList?._id } });
          }}
        >
          Delete Selected Price List
        </Button>
        <input style={{ display: 'none' }} id="upload-excel" name="upload-excel" onChange={(e) => readUploadFile(e)} type="file" />
        <label htmlFor="upload-excel">
          <Button sx={{ mr: 1 }} color="primary" variant="contained" component="span">
            Upload Excel
          </Button>
        </label>
        <Button sx={{ mr: 1 }} variant="contained" color="success" onClick={() => createList()}>
          Create New Price List
        </Button>
        <Button variant="contained" color="success" onClick={() => viewSelected()}>
          View Selected Price List
        </Button>
      </FlexEnd>
    </Box>
  );
};

const GETPRICELISTS = gql`
  query priceListMany($filter: FilterFindManyPriceListInput) {
    priceListMany(filter: $filter, limit: 10000) {
      _id
      name
      project {
        _id
      }
      draft {
        _id
        status
        suite
        unit
        unitId
        level
        modelType
        unitType
        basePrice
        size
        outdoor {
          name
          size
        }
        bathroom
        exposure
        ceilingHeight
        custom
        col
        row
        stackCol
        stackRow
      }
    }
  }
`;

const UPDATEPRICELIST = gql`
  mutation priceListUpdateById($_id: MongoID!, $record: UpdateByIdPriceListInput!) {
    priceListUpdateById(_id: $_id, record: $record) {
      record {
        _id
        name
        project {
          _id
        }
        draft {
          _id
          status
          suite
          unit
          unitId
          level
          modelType
          unitType
          basePrice
          size
          outdoor {
            name
            size
          }
          bathroom
          exposure
          ceilingHeight
          custom
          col
          row
          stackCol
          stackRow
        }
      }
    }
  }
`;

const DELETEPRICELIST = gql`
  mutation priceListRemoveById($_id: MongoID!) {
    priceListRemoveById(_id: $_id) {
      record {
        _id
        name
        project {
          _id
        }
        draft {
          _id
          status
          suite
          unit
          unitId
          level
          modelType
          unitType
          basePrice
          size
          outdoor {
            name
            size
          }
          bathroom
          exposure
          ceilingHeight
          custom
          col
          row
          stackCol
          stackRow
        }
      }
    }
  }
`;

const CREATEPRICELIST = gql`
  mutation priceListCreateOne($record: CreateOnePriceListInput!) {
    priceListCreateOne(record: $record) {
      record {
        _id
        name
        project {
          _id
        }
        draft {
          _id
          status
          suite
          unit
          unitId
          level
          modelType
          unitType
          basePrice
          size
          outdoor {
            name
            size
          }
          bathroom
          exposure
          ceilingHeight
          custom
          col
          row
          stackCol
          stackRow
        }
      }
    }
  }
`;

export default Draft;
